/**
 * Groups an array of items by a specified criteria.
 *
 * @param {Array} array - The array of items to group.
 * @param {string|Function} criteria - The key name to group by if string, or a function that returns the grouping key.
 * @returns {any} An object where keys are the group identifiers and values are arrays of items belonging to that group.
 *
 * @example
 * const items = [{ type: 'fruit', name: 'apple' }, { type: 'fruit', name: 'banana' }];
 * groupBy(items, 'type'); // { fruit: [{ type: 'fruit', name: 'apple' }, { type: 'fruit', name: 'banana' }] }
 * groupBy(items, item => item.type); // { fruit: [{ type: 'fruit', name: 'apple' }, { type: 'fruit', name: 'banana' }] }
 */
export const groupBy = (array, criteria) =>
  array.reduce((obj, item) => {
    const key =
      typeof criteria === "function" ? criteria(item) : item[criteria];

    // eslint-disable-next-line no-prototype-builtins
    if (!obj.hasOwnProperty(key)) {
      obj[key] = [];
    }

    obj[key].push(item);
    return obj;
  }, {});

/**
 * Groups an array of items by a specified criteria and includes additional metadata for each group.
 *
 * @param {Array} array - The array of items to group.
 * @param {string|Function} criteria - The key name to group by if string, or a function that returns the grouping key.
 * @param {Function} [paramsCriteria] - Optional function that returns additional metadata/params to include with each group.
 * @returns {any} An object where keys are the group identifiers and values are objects containing a 'data' array and any additional metadata.
 *
 * @example
 * const items = [{ type: 'fruit', name: 'apple', count: 5 }];
 * groupArrayByKeyWithMetadata(items, 'type', item => ({ totalCount: item.count }));
 * // { fruit: { data: [{ type: 'fruit', name: 'apple', count: 5 }], totalCount: 5 } }
 */
export const groupArrayByKeyWithMetadata = (
  array,
  criteria,
  paramsCriteria
) => {
  return array.reduce((obj, item) => {
    const key =
      (typeof criteria === "function" ? criteria(item) : item[criteria]) ||
      "Unknown";
    const params = paramsCriteria ? paramsCriteria(item) : {};

    if (!Object.hasOwn(obj, key)) {
      obj[key] = { data: [], ...params };
    }

    obj[key].data.push(item);
    return obj;
  }, {});
};
